import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  fetchCargos,
  removeCargosFromStore,
  setCargosGroup,
  setCurrentCargo,
  setIsOnlyCargo,
  setIsVisibleDeleteManyModalCargo,
  setIsVisibleDeleteModalCargo,
  setIsVisibleModalCargo,
  setSelectedCargosForDeletion,
} from '../../../redux/reducers/cargos'
import {
  fetchAllInvoices,
  fetchAttachCargo,
  setIsVisibleCreateModalInvoice,
} from '../../../redux/reducers/invoices'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { invoicesSelector } from '../../../redux/selectors/invoices-selector'
import { AppDispatch } from '../../../redux/store'
import { formatDateToDDMMYYYY } from '../../../utils/date'
import { getItemCurrency } from '../../../utils/getAmount'
import { getRole } from '../../../utils/getRole'
import axiosInstance from '../../../utils/axiosInstance'
import { URL } from '../../../constants/url'
import InvoiceCreateModal from '../invoice-modal/InvoiceCreateModal'
import UnpaidCargoCard from '../cargo-side-modal/UnpaidCargoCard'
import UnpaidCargosActionsPanel from '../cargo-side-modal/UnpaidCargosActionsPanel'
import UnpaidCargosHeader from '../cargo-side-modal/UnpaidCargosHeader'
import styles from './AddClientModal.module.css'
import type { Cargo } from '../../../redux/reducers/cargos'

const ClientToPayTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { isClient } = getRole()

  const { cargos, isVisibleDeleteManyModalCargo, isVisibleModalCargo } =
    useSelector(cargosSelector)
  const {
    allInvoices,
    isPendingGetAllInvoices,
    isVisibleCreateModalInvoice,
    isVisibleModalInvoice,
  } = useSelector(invoicesSelector)

  const [selectedCargos, setSelectedCargos] = useState<number[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState('')
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('')
  const [isOpenedDropdown, setOpenedDropdown] = useState(false)

  const clientCargos = useMemo(() => {
    if (!clientId || !cargos) return []
    return cargos[clientId.toString()] || []
  }, [cargos, clientId])

  const clientInvoices = useMemo(
    () => allInvoices.filter((item) => item.client_id === clientId),
    [allInvoices, clientId]
  )

  const cargoModalWasOpen = useRef(false)
  const invoiceModalWasOpen = useRef(false)

  const reloadLists = useCallback(() => {
    dispatch(fetchCargos())
    dispatch(fetchAllInvoices({ client: clientId.toString() }))
  }, [clientId, dispatch])

  useEffect(() => {
    reloadLists()
  }, [reloadLists])

  useEffect(() => {
    if (cargoModalWasOpen.current && !isVisibleModalCargo) reloadLists()
    cargoModalWasOpen.current = isVisibleModalCargo
  }, [isVisibleModalCargo, reloadLists])

  useEffect(() => {
    const invoiceOpen = isVisibleModalInvoice || isVisibleCreateModalInvoice
    if (invoiceModalWasOpen.current && !invoiceOpen) reloadLists()
    invoiceModalWasOpen.current = invoiceOpen
  }, [isVisibleCreateModalInvoice, isVisibleModalInvoice, reloadLists])

  useEffect(() => {
    dispatch(setCargosGroup(clientCargos))
  }, [clientCargos, dispatch])

  useEffect(() => {
    if (!isVisibleDeleteManyModalCargo) {
      setSelectedCargos([])
      setSelectAll(false)
    }
  }, [isVisibleDeleteManyModalCargo])

  useEffect(() => {
    return () => {
      dispatch(setIsOnlyCargo(false))
    }
  }, [dispatch])

  const handleCargoSelect = (cargoId: number) => {
    setSelectedCargos((prev) =>
      prev.includes(cargoId)
        ? prev.filter((id) => id !== cargoId)
        : [...prev, cargoId]
    )
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCargos([])
    } else {
      setSelectedCargos(clientCargos.map((cargo) => cargo.id))
    }
    setSelectAll(!selectAll)
  }

  const totalSelectedAmount = useMemo(
    () =>
      clientCargos
        .filter((cargo) => selectedCargos.includes(cargo.id))
        .reduce((sum, cargo) => sum + cargo.amount, 0),
    [clientCargos, selectedCargos]
  )

  const commonTotalAmount = useMemo(
    () => clientCargos.reduce((sum, cargo) => sum + cargo.amount, 0),
    [clientCargos]
  )

  const getCurrency = useCallback(() => {
    return getItemCurrency(clientCargos[0]?.currency || '')
  }, [clientCargos])

  const handleSetInvoice = (id: number, date: string) => {
    setSelectedInvoice(`№${id} от ${formatDateToDDMMYYYY(date)}`)
    setSelectedInvoiceId(String(id))
    setOpenedDropdown(false)
  }

  const handleEditCargo = async (item: Cargo) => {
    try {
      const response = await axiosInstance.get(`${URL.cargos}/${item.id}`)
      dispatch(setCurrentCargo(response.data))
      dispatch(setIsOnlyCargo(true))
      dispatch(setIsVisibleModalCargo(true))
    } catch {
      toast.error('Не удалось открыть груз')
    }
  }

  const handleDeleteCargo = (item: Cargo) => {
    dispatch(setIsOnlyCargo(true))
    dispatch(setIsVisibleDeleteModalCargo(true))
    dispatch(setCurrentCargo(item))
    setSelectedCargos([])
    setSelectedInvoice('')
  }

  const handleCreateInvoice = () => {
    dispatch(setCargosGroup(clientCargos))
    dispatch(setIsVisibleCreateModalInvoice(true))
  }

  const handleAttachInvoice = () => {
    dispatch(
      fetchAttachCargo({ invoiceId: selectedInvoiceId, selectedCargos })
    ).then((res) => {
      //@ts-ignore
      if (!res.error) {
        dispatch(removeCargosFromStore({ cargoIds: selectedCargos }))
        setSelectedCargos([])
        setSelectedInvoice('')
        setSelectedInvoiceId('')
      }
    })
  }

  const handleDeleteMany = () => {
    if (!selectedCargos.length) return
    dispatch(setSelectedCargosForDeletion(selectedCargos))
    dispatch(setIsVisibleDeleteManyModalCargo(true))
  }

  return (
    <>
      <UnpaidCargosHeader
        checkboxId="client_shipment_all"
        selectAll={selectAll}
        onSelectAll={handleSelectAll}
        disabled={!clientCargos.length}
        totalAmount={commonTotalAmount}
        currency={getCurrency()}
      />

      {!clientCargos.length && (
        <div
          className={classNames(styles.small2, 'pt-2 pb-3')}
          style={{ textAlign: 'center' }}
        >
          <span className={styles.colorGray}>Нет грузов к оплате</span>
        </div>
      )}

      {clientCargos.map((item) => (
        <UnpaidCargoCard
          key={item.id}
          item={item}
          isSelected={selectedCargos.includes(item.id)}
          isClient={isClient}
          checkboxIdPrefix="client_shipment"
          onToggleSelect={handleCargoSelect}
          onEdit={handleEditCargo}
          onDelete={handleDeleteCargo}
        />
      ))}

      {!isClient && (
        <UnpaidCargosActionsPanel
          totalSelectedAmount={totalSelectedAmount}
          currency={getCurrency()}
          selectedCount={selectedCargos.length}
          onCreateInvoice={handleCreateInvoice}
          invoices={clientInvoices}
          isPendingGetAllInvoices={isPendingGetAllInvoices}
          selectedInvoiceLabel={selectedInvoice}
          selectedInvoiceId={selectedInvoiceId}
          isOpenedDropdown={isOpenedDropdown}
          setOpenedDropdown={setOpenedDropdown}
          onSelectInvoice={handleSetInvoice}
          onAttach={handleAttachInvoice}
          onDeleteMany={handleDeleteMany}
        />
      )}

      {isVisibleCreateModalInvoice && (
        <InvoiceCreateModal
          setSelectedCargos={setSelectedCargos}
          selectedCargos={selectedCargos}
        />
      )}
    </>
  )
}

export default ClientToPayTab
